import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from './api';
import type { WeekDecision } from './timesheet-api';

export type SubmissionStatus = 'NOT_SUBMITTED' | 'SUBMITTED' | 'APPROVED' | 'DENIED';

export interface PendingWeek {
  id: string;
  weekStart: string;
  weekEnd: string;
  /** What this row covers: the week, or one part of a week split at a month end. */
  periodStart: string;
  periodEnd: string;
  expectedSeconds: number;
  actualSeconds: number | null;
  submittedAt: string | null;
  employee: { id: string; displayName: string };
}

export interface MyWeek {
  id: string;
  weekStart: string;
  weekEnd: string;
  /** What this row covers: the week, or one part of a week split at a month end. */
  periodStart: string;
  periodEnd: string;
  status: SubmissionStatus;
  expectedSeconds: number;
  actualSeconds: number | null;
  submittedAt: string | null;
  decidedAt: string | null;
  /** Who decided it and what they said. Null while nobody has. */
  decision: WeekDecision | null;
}

export const usePendingApprovals = () =>
  useQuery({
    queryKey: ['approvals', 'pending'],
    queryFn: () => api.get<PendingWeek[]>('/approvals/pending'),
  });

export const useMySubmissions = () =>
  useQuery({
    queryKey: ['approvals', 'mine'],
    queryFn: () => api.get<MyWeek[]>('/approvals/mine'),
  });

/** A finished week, or part of one, not yet submitted - oldest first. */
export interface OutstandingWeek {
  start: string;
  end: string;
  status: 'NOT_SUBMITTED' | 'DENIED';
  actualSeconds: number;
  expectedSeconds: number;
}

export const useOutstandingWeeks = (enabled: boolean) =>
  useQuery({
    queryKey: ['approvals', 'outstanding'],
    queryFn: () => api.get<OutstandingWeek[]>('/approvals/mine/outstanding'),
    enabled,
  });

/**
 * Every approval action invalidates the timesheet cache as well as its own.
 *
 * Submitting and reopening both move the lockdown boundary, which changes the
 * `locked` flag on days the week grid is already showing. Refreshing only the
 * approvals list would leave the grid offering Edit buttons on a week that has
 * just been closed.
 */
function useApprovalMutation<TArgs>(fn: (args: TArgs) => Promise<unknown>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['approvals'] });
      void queryClient.invalidateQueries({ queryKey: ['timesheet'] });
    },
  });
}

export const useSubmitWeek = () =>
  useApprovalMutation((date: string) => api.post('/approvals/submit', { date }));

export const useApproveWeek = () =>
  useApprovalMutation((id: string) => api.post(`/approvals/${id}/approve`, {}));

export const useDenyWeek = () =>
  useApprovalMutation(({ id, message }: { id: string; message: string }) =>
    api.post(`/approvals/${id}/deny`, { message }),
  );

export interface DecidedWeek {
  id: string;
  weekStart: string;
  weekEnd: string;
  /** What this row covers: the week, or one part of a week split at a month end. */
  periodStart: string;
  periodEnd: string;
  status: 'APPROVED' | 'DENIED';
  expectedSeconds: number;
  actualSeconds: number | null;
  decidedAt: string | null;
  employee: { id: string; displayName: string };
}

/**
 * Weeks this approver has already decided.
 *
 * Exists so a decision can be undone. The app tells people to "ask your supervisor
 * to reopen it", and until this there was no screen on which a supervisor could
 * find a decided week to reopen.
 */
export const useDecidedWeeks = () =>
  useQuery({
    queryKey: ['approvals', 'decided'],
    queryFn: () => api.get<DecidedWeek[]>('/approvals/decided'),
  });

export const useReopenWeek = () =>
  useApprovalMutation(({ id, message }: { id: string; message?: string }) =>
    api.post(`/approvals/${id}/reopen`, { message: message ?? null }),
  );

export interface BulkApproveResult {
  approved: number;
  failed: { id: string; message: string }[];
}

export const useBulkApprove = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) =>
      api.post<BulkApproveResult>('/approvals/bulk-approve', { ids }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['approvals'] });
      void queryClient.invalidateQueries({ queryKey: ['timesheet'] });
    },
  });
};

export interface DenyEmptyResult {
  denied: number;
  failed: string[];
}

/**
 * Send back every submitted week that is over and completely empty.
 *
 * Takes no arguments on purpose: the server decides which weeks qualify, so the
 * browser cannot widen "empty and finished" into something else.
 */
export const useDenyEmptyWeeks = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<DenyEmptyResult>('/approvals/deny-empty', {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['approvals'] });
      void queryClient.invalidateQueries({ queryKey: ['timesheet'] });
    },
  });
};

/** One person's row on the board: a cell per finished week. */
export interface BoardCell {
  weekStart: string;
  /** NOT_APPLICABLE = the contract expected no work, so nothing can be late. */
  status: SubmissionStatus | 'NOT_APPLICABLE';
  actualSeconds: number;
  expectedSeconds: number;
  submissionId: string | null;
  /** One per part: two on a week split at a month end, each with its own status. */
  parts: BoardPart[];
}

export interface BoardPart {
  start: string;
  end: string;
  /** NOT_APPLICABLE here also covers a part that has not ended yet. */
  status: SubmissionStatus | 'NOT_APPLICABLE';
  actualSeconds: number;
  expectedSeconds: number;
  submissionId: string | null;
}

export interface BoardRow {
  employee: { id: string; displayName: string };
  cells: BoardCell[];
  /** Weeks not submitted, or sent back and not resubmitted. */
  missing: number;
}

export interface Board {
  weeks: string[];
  rows: BoardRow[];
  /** How many people have at least one week outstanding. */
  behind: number;
}

export const useApprovalBoard = (weeks: number) =>
  useQuery({
    queryKey: ['approvals', 'board', weeks],
    queryFn: () => api.get<Board>(`/approvals/board?weeks=${weeks}`),
  });
